import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent } from "../ui/dialog";
import { cn } from "../../lib/utils";
import {
  Database,
  TrendingUp,
  LineChart,
  Cpu,
  Target,
  MessageCircle,
  Upload,
  ScrollText,
  Search,
} from "lucide-react";

const commands = [
  { id: "stock", label: "Stock", shortcut: "Données", to: "/stock", icon: Database },
  { id: "stockprices", label: "Stock Prices", shortcut: "Données", to: "/stockprices", icon: TrendingUp },
  { id: "yahoo", label: "Yahoo Finance", shortcut: "Données", to: "/yahoo-finance", icon: LineChart },
  { id: "predictor", label: "Predictor", shortcut: "Prédictions", to: "/predictor", icon: Cpu },
  { id: "prediction", label: "Prediction", shortcut: "Prédictions", to: "/prediction", icon: Target },
  { id: "comment", label: "Comment", shortcut: "Prédictions", to: "/comment", icon: MessageCircle },
  { id: "import-stocktwits", label: "Import Stocktwits", shortcut: "Outils", to: "/import-stocktwits", icon: Upload },
  { id: "import-logs", label: "Import Logs", shortcut: "Outils", to: "/import-logs", icon: ScrollText },
];

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const navigate = useNavigate();
  const inputRef = useRef(null);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  useEffect(() => {
    window.__openCommandPalette = () => setOpen(true);
    return () => {
      delete window.__openCommandPalette;
    };
  }, []);

  const filtered = commands.filter(
    (c) =>
      c.label.toLowerCase().includes(query.toLowerCase()) ||
      c.shortcut.toLowerCase().includes(query.toLowerCase())
  );

  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  const runCommand = useCallback(
    (index) => {
      const cmd = filtered[index];
      if (cmd) {
        navigate(cmd.to);
        setOpen(false);
        setQuery("");
      }
    },
    [filtered, navigate]
  );

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev + 1) % filtered.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev - 1 + filtered.length) % filtered.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      runCommand(selectedIndex);
    }
  };

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="[&>button]:hidden gap-0 p-0 overflow-hidden max-w-lg">
        <div className="flex items-center border-b px-3">
          <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Rechercher une page ou une action..."
            className="flex h-11 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
          />
        </div>
        <div className="max-h-[300px] overflow-y-auto py-1 px-1">
          {filtered.length === 0 && (
            <div className="py-6 text-center text-sm text-muted-foreground">
              Aucun résultat.
            </div>
          )}
          {filtered.map((cmd, index) => {
            const Icon = cmd.icon;
            return (
              <button
                key={cmd.id}
                onClick={() => runCommand(index)}
                className={cn(
                  "relative flex w-full cursor-pointer select-none items-center rounded-sm px-2 py-2 text-sm outline-none transition-colors",
                  index === selectedIndex
                    ? "bg-accent text-accent-foreground"
                    : "hover:bg-accent hover:text-accent-foreground"
                )}
              >
                {Icon && <Icon className="mr-2 h-4 w-4" />}
                <span className="flex-1">{cmd.label}</span>
                <span className="text-xs text-muted-foreground">{cmd.shortcut}</span>
              </button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
